import React, { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  MoreVertical,
  Pause,
  Play,
  DollarSign,
  Zap,
  Copy,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { SpendEfficiencyMetric, QuickAction } from '../../../shared/types/dashboard';

interface SpendEfficiencyTableProps {
  campaigns: SpendEfficiencyMetric[];
  onQuickAction: (campaignId: string, action: QuickAction) => Promise<void>;
  onCampaignClick?: (campaignId: string) => void;
  isLoading?: boolean;
}

export const SpendEfficiencyTable: React.FC<SpendEfficiencyTableProps> = ({
  campaigns,
  onQuickAction,
  onCampaignClick,
  isLoading = false
}) => {
  const [sortColumn, setSortColumn] = useState<keyof SpendEfficiencyMetric>('cost_per_stream');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const quickActions: QuickAction[] = [
    {
      id: 'pause',
      label: 'Pause Campaign',
      icon: 'pause',
      type: 'pause',
      handler: async (campaignId) => {
        // Handler will be implemented in parent
        return { success: true, action_type: 'pause', message: 'Campaign paused' };
      },
      disabled_when: (campaign) => campaign.status === 'paused'
    },
    {
      id: 'resume',
      label: 'Resume Campaign',
      icon: 'play',
      type: 'resume',
      handler: async (campaignId) => {
        return { success: true, action_type: 'resume', message: 'Campaign resumed' };
      },
      disabled_when: (campaign) => campaign.status === 'active'
    },
    {
      id: 'boost',
      label: 'Boost Budget +25%',
      icon: 'trending-up',
      type: 'boost',
      handler: async (campaignId) => {
        return { success: true, action_type: 'boost', message: 'Budget increased by 25%' };
      },
      disabled_when: (campaign) => campaign.health_score === 'poor'
    },
    {
      id: 'decrease',
      label: 'Decrease Budget -25%',
      icon: 'trending-down',
      type: 'decrease',
      handler: async (campaignId) => {
        return { success: true, action_type: 'decrease', message: 'Budget decreased by 25%' };
      },
      disabled_when: (campaign) => campaign.health_score === 'good'
    },
    {
      id: 'clone',
      label: 'Clone Campaign',
      icon: 'copy',
      type: 'clone',
      handler: async (campaignId) => {
        return { success: true, action_type: 'clone', message: 'Campaign cloned' };
      }
    },
    {
      id: 'details',
      label: 'View Details',
      icon: 'external-link',
      type: 'details',
      handler: async (campaignId) => {
        return { success: true, action_type: 'details', message: 'Opening details' };
      }
    }
  ];

  const sortedCampaigns = useMemo(() => {
    const sorted = [...campaigns].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Handle nested values
      if (sortColumn === 'cost_per_stream' || sortColumn === 'today_spend' || sortColumn === 'streams_generated') {
        aValue = aValue as number;
        bValue = bValue as number;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [campaigns, sortColumn, sortDirection]);

  const handleSort = (column: keyof SpendEfficiencyMetric) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleQuickAction = async (campaignId: string, action: QuickAction, campaign: SpendEfficiencyMetric) => {
    if (action.disabled_when?.(campaign)) return;
    
    setProcessingAction(`${campaignId}-${action.id}`);
    try {
      await onQuickAction(campaignId, action);
      setActiveDropdown(null);
    } catch (error) {
      console.error('Failed to execute quick action:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const getTrendIcon = (trend: SpendEfficiencyMetric['trend']) => {
    if (trend.direction === 'up') {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (trend.direction === 'down') {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getHealthColor = (health: SpendEfficiencyMetric['health_score']) => {
    switch (health) {
      case 'good':
        return 'bg-green-100 dark:bg-green-900/20';
      case 'average':
        return 'bg-yellow-100 dark:bg-yellow-900/20';
      case 'poor':
        return 'bg-red-100 dark:bg-red-900/20';
    }
  };

  const getHealthBadge = (health: SpendEfficiencyMetric['health_score']) => {
    switch (health) {
      case 'good':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'average':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'poor':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const SortIcon = ({ column }: { column: keyof SpendEfficiencyMetric }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3" />
      : <ArrowDown className="h-3 w-3" />;
  };

  const Sparkline = ({ data }: { data?: number[] }) => {
    if (!data || data.length === 0) return null;
    
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 60;
    const height = 20;
    
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');
    
    return (
      <svg width={width} height={height} className="inline-block">
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-blue-500 dark:text-blue-400"
        />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="text-center py-8">
          <DollarSign className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No active campaigns</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Start a campaign to see spend efficiency metrics here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Spend Efficiency
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Real-time ROI across all active campaigns
        </p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('campaign_name')}
              >
                <div className="flex items-center gap-2">
                  Campaign
                  <SortIcon column="campaign_name" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('today_spend')}
              >
                <div className="flex items-center gap-2">
                  Today's Spend
                  <SortIcon column="today_spend" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('streams_generated')}
              >
                <div className="flex items-center gap-2">
                  Streams
                  <SortIcon column="streams_generated" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => handleSort('cost_per_stream')}
              >
                <div className="flex items-center gap-2">
                  Cost/Stream
                  <SortIcon column="cost_per_stream" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Trend
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                7-Day
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedCampaigns.map((campaign) => (
              <tr 
                key={campaign.campaign_id}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${getHealthColor(campaign.health_score)}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="cursor-pointer"
                    onClick={() => onCampaignClick?.(campaign.campaign_id)}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {campaign.campaign_name}
                    </div>
                    {campaign.artist_name && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {campaign.artist_name}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {formatCurrency(campaign.today_spend)}
                  </div>
                  {campaign.yesterday_spend && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      vs {formatCurrency(campaign.yesterday_spend)}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {formatNumber(campaign.streams_generated)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(campaign.cost_per_stream)}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getHealthBadge(campaign.health_score)}`}>
                      {campaign.health_score}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getTrendIcon(campaign.trend)}
                    <span className={`text-sm font-medium ${
                      campaign.trend.direction === 'up' ? 'text-green-600 dark:text-green-400' :
                      campaign.trend.direction === 'down' ? 'text-red-600 dark:text-red-400' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                      {campaign.trend.percentage}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Sparkline data={campaign.sparkline_data} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === campaign.campaign_id ? null : campaign.campaign_id)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                    
                    {activeDropdown === campaign.campaign_id && (
                      <div className="absolute right-0 z-10 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5">
                        <div className="py-1">
                          {quickActions.map((action) => {
                            const isDisabled = action.disabled_when?.(campaign);
                            const isProcessing = processingAction === `${campaign.campaign_id}-${action.id}`;
                            
                            return (
                              <button
                                key={action.id}
                                onClick={() => handleQuickAction(campaign.campaign_id, action, campaign)}
                                disabled={isDisabled || isProcessing}
                                className={`
                                  block w-full text-left px-4 py-2 text-sm
                                  ${isDisabled 
                                    ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
                                  }
                                `}
                              >
                                <div className="flex items-center gap-2">
                                  {action.type === 'pause' && <Pause className="h-4 w-4" />}
                                  {action.type === 'resume' && <Play className="h-4 w-4" />}
                                  {action.type === 'boost' && <TrendingUp className="h-4 w-4" />}
                                  {action.type === 'decrease' && <TrendingDown className="h-4 w-4" />}
                                  {action.type === 'clone' && <Copy className="h-4 w-4" />}
                                  {action.type === 'details' && <ExternalLink className="h-4 w-4" />}
                                  <span>{isProcessing ? 'Processing...' : action.label}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
